import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Zap, CalendarClock, Loader2, AlertTriangle } from "lucide-react";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\+\d{1,3}\s?\d{6,14}$/;

export default function ReportForm({ agent = null, timeSlots = [] }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [contactMode, setContactMode] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    agent_name: agent ? `${agent.name} ${agent.surname}` : "",
    agent_code: agent ? agent.agent_code : "",
    agent_phone: agent?.phone || "",
    agent_email: agent?.email || "",
    company_name: "",
    contact_name: "",
    phone: "+39 ",
    email: "",
    vat: "",
    location: "",
    preferred_date: "",
    preferred_time_slot: "",
    notes: "",
    privacy_consent: false,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (agent) {
      setForm((f) => ({
        ...f,
        agent_name: `${agent.name} ${agent.surname}`,
        agent_code: agent.agent_code,
        agent_phone: agent.phone || "",
        agent_email: agent.email || "",
      }));
    }
  }, [agent]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.agent_name.trim()) e.agent_name = "Campo obbligatorio";
    if (!form.company_name.trim()) e.company_name = "Campo obbligatorio";
    if (!form.contact_name.trim()) e.contact_name = "Campo obbligatorio";
    if (!phoneRegex.test(form.phone.replace(/\s/g, "").length ? form.phone.replace(/\s+/g, " ").trim() : "")) {
      const cleaned = form.phone.replace(/\s+/g, "");
      if (!/^\+\d{8,16}$/.test(cleaned)) e.phone = "Numero di telefono non valido";
    }
    if (!emailRegex.test(form.email)) e.email = "Email non valida";
    if (form.agent_email && !emailRegex.test(form.agent_email)) e.agent_email = "Email agente non valida";
    if (!contactMode) e.contact_mode = "Seleziona una modalità di contatto";
    if (contactMode === "scheduled") {
      if (!form.preferred_date) e.preferred_date = "Seleziona una data";
      else if (form.preferred_date < today) e.preferred_date = "La data non può essere nel passato";
      if (!form.preferred_time_slot) e.preferred_time_slot = "Seleziona una fascia oraria";
    }
    if (!form.privacy_consent) e.privacy_consent = "Consenso obbligatorio";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) {
      toast.error("Controlla i campi evidenziati");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        contact_mode: contactMode,
        agent_slug: agent ? agent.agent_code : null,
        preferred_date: contactMode === "scheduled" ? form.preferred_date : null,
        preferred_time_slot: contactMode === "scheduled" ? form.preferred_time_slot : null,
      };
      const res = await api.post("/requests", payload);
      navigate("/conferma", { state: { result: res.data } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante l'invio");
      setSubmitting(false);
    }
  };

  const inputCls = "bg-white border-gray-300 focus-visible:ring-[#FFCC00] h-12 rounded-xl";

  return (
    <section id="form" className="py-20 md:py-28 bg-[#F9FAFB] border-t border-gray-200">
      <div className="max-w-3xl mx-auto px-5">
        <div className="mb-10">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#111111]" data-testid="form-title">
            Segnala il cliente interessato
          </h2>
          <p className="mt-3 text-gray-600 text-base md:text-lg">
            Inserisci i dati del cliente. Un operatore Dopopay lo contatterà per completare l'attivazione.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-8" data-testid="report-form">
          {/* Dati agente */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
            <h3 className="font-display font-semibold text-lg mb-5 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#FFCC00] text-black text-sm flex items-center justify-center font-bold">1</span>
              Dati dell'agente
            </h3>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className="mb-1.5 block">Nome e cognome agente *</Label>
                <Input data-testid="agent-name-input" className={inputCls} value={form.agent_name} disabled={!!agent}
                  onChange={(e) => set("agent_name", e.target.value)} />
                {errors.agent_name && <p className="text-xs text-red-500 mt-1">{errors.agent_name}</p>}
              </div>
              <div>
                <Label className="mb-1.5 block">Telefono agente</Label>
                <Input data-testid="agent-phone-input" className={inputCls} value={form.agent_phone} disabled={!!agent}
                  onChange={(e) => set("agent_phone", e.target.value)} placeholder="Opzionale" />
              </div>
              <div className="md:col-span-2">
                <Label className="mb-1.5 block">Email agente</Label>
                <Input data-testid="agent-email-input" type="email" className={inputCls} value={form.agent_email} disabled={!!agent && !!agent.email}
                  onChange={(e) => set("agent_email", e.target.value)} placeholder="Riceverai una copia della segnalazione" />
                {errors.agent_email && <p className="text-xs text-red-500 mt-1">{errors.agent_email}</p>}
              </div>
            </div>
            {agent && <p className="text-xs text-gray-500 mt-3">Dati agente compilati automaticamente dal link personale.</p>}
          </div>

          {/* Dati cliente */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
            <h3 className="font-display font-semibold text-lg mb-5 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#FFCC00] text-black text-sm flex items-center justify-center font-bold">2</span>
              Dati del cliente
            </h3>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <Label className="mb-1.5 block">Ragione sociale *</Label>
                <Input data-testid="company-name-input" className={inputCls} value={form.company_name}
                  onChange={(e) => set("company_name", e.target.value)} />
                {errors.company_name && <p className="text-xs text-red-500 mt-1">{errors.company_name}</p>}
              </div>
              <div>
                <Label className="mb-1.5 block">Nome e cognome referente *</Label>
                <Input data-testid="contact-name-input" className={inputCls} value={form.contact_name}
                  onChange={(e) => set("contact_name", e.target.value)} />
                {errors.contact_name && <p className="text-xs text-red-500 mt-1">{errors.contact_name}</p>}
              </div>
              <div>
                <Label className="mb-1.5 block">Numero di telefono *</Label>
                <Input data-testid="phone-input" className={inputCls} value={form.phone}
                  onChange={(e) => set("phone", e.target.value)} placeholder="+39 333 1234567" />
                {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
              </div>
              <div>
                <Label className="mb-1.5 block">Email *</Label>
                <Input data-testid="email-input" type="email" className={inputCls} value={form.email}
                  onChange={(e) => set("email", e.target.value)} />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>
            </div>
          </div>

          {/* Modalità di contatto */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
            <h3 className="font-display font-semibold text-lg mb-5 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#FFCC00] text-black text-sm flex items-center justify-center font-bold">3</span>
              Modalità di contatto
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <button type="button" data-testid="contact-immediate-btn" onClick={() => setContactMode("immediate")}
                className={`text-left p-5 rounded-xl border-2 transition-colors duration-200 ${contactMode === "immediate" ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-200 hover:border-gray-300"}`}>
                <div className="flex items-center gap-2 font-semibold"><Zap className="w-5 h-5 text-[#E6B800]" /> Il cliente è disponibile adesso</div>
                <p className="text-sm text-gray-600 mt-2">Richiedi di essere ricontattato il prima possibile.</p>
              </button>
              <button type="button" data-testid="contact-scheduled-btn" onClick={() => setContactMode("scheduled")}
                className={`text-left p-5 rounded-xl border-2 transition-colors duration-200 ${contactMode === "scheduled" ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-200 hover:border-gray-300"}`}>
                <div className="flex items-center gap-2 font-semibold"><CalendarClock className="w-5 h-5 text-[#E6B800]" /> Scegli giorno e orario</div>
                <p className="text-sm text-gray-600 mt-2">Programma il contatto in base alla disponibilità.</p>
              </button>
            </div>
            {errors.contact_mode && <p className="text-xs text-red-500 mt-2">{errors.contact_mode}</p>}

            {contactMode === "immediate" && (
              <div className="mt-4 text-sm bg-[#FFFBEB] border border-[#FFE58A] rounded-xl p-4" data-testid="immediate-note">
                La richiesta sarà presa in carico il prima possibile durante gli orari di assistenza.
              </div>
            )}

            {contactMode === "scheduled" && (
              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <div>
                  <Label className="mb-1.5 block">Data preferita *</Label>
                  <Input data-testid="date-input" type="date" min={today} className={inputCls} value={form.preferred_date}
                    onChange={(e) => set("preferred_date", e.target.value)} />
                  {errors.preferred_date && <p className="text-xs text-red-500 mt-1">{errors.preferred_date}</p>}
                </div>
                <div>
                  <Label className="mb-1.5 block">Fascia oraria preferita *</Label>
                  <Select value={form.preferred_time_slot} onValueChange={(v) => set("preferred_time_slot", v)}>
                    <SelectTrigger data-testid="timeslot-select" className={inputCls}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.preferred_time_slot && <p className="text-xs text-red-500 mt-1">{errors.preferred_time_slot}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Note + privacy */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 space-y-5">
            <div>
              <Label className="mb-1.5 block">Note dell'agente</Label>
              <Textarea data-testid="notes-input" rows={3} className="bg-white border-gray-300 focus-visible:ring-[#FFCC00] rounded-xl"
                value={form.notes} onChange={(e) => set("notes", e.target.value)}
                placeholder="Inserisci eventuali informazioni utili sul cliente o sulla richiesta." />
            </div>
            <div className="flex items-start gap-3">
              <Checkbox data-testid="privacy-checkbox" id="privacy" checked={form.privacy_consent}
                onCheckedChange={(v) => set("privacy_consent", !!v)} className="mt-1 data-[state=checked]:bg-[#FFCC00] data-[state=checked]:border-[#FFCC00] data-[state=checked]:text-black" />
              <label htmlFor="privacy" className="text-sm text-gray-700 leading-relaxed">
                Confermo di essere autorizzato a comunicare i dati inseriti e dichiaro che il cliente è stato informato del successivo contatto da parte di Dopopay.
                <span className="block mt-1 text-gray-500">
                  <a href="#" className="underline hover:text-black">Informativa privacy</a> · <a href="#" className="underline hover:text-black">Termini e condizioni</a> · <a href="#" className="underline hover:text-black">Cookie policy</a>
                </span>
              </label>
            </div>
            {errors.privacy_consent && <p className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{errors.privacy_consent}</p>}
          </div>

          <Button type="submit" data-testid="submit-request-btn" disabled={submitting}
            className="w-full h-14 text-base font-semibold rounded-xl bg-[#111111] hover:bg-[#000] text-white transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99]">
            {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Invio in corso…</> : "Invia richiesta di attivazione"}
          </Button>
        </form>
      </div>
    </section>
  );
}
